/**
 * Resend Email Button Component
 * Handles resending verification email with rate limiting and countdown
 */

import React, { useState, useEffect } from 'react'
import { useEmailVerification } from '../../hooks/useEmailVerification'
import type { ResendEmailErrorResponse } from '../../types/email-verification'

interface ResendEmailButtonProps {
  className?: string
  style?: React.CSSProperties
}

export function ResendEmailButton({ className, style }: ResendEmailButtonProps) {
  const { resend } = useEmailVerification(null)
  const [countdown, setCountdown] = useState(0)

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown])

  // Handle resend button click
  const handleResend = () => {
    resend.mutate(undefined, {
      onError: (error: ResendEmailErrorResponse) => {
        if (error.code === 'TOO_FREQUENT' && error.retryAfter) {
          setCountdown(error.retryAfter)
        }
      },
    })
  }

  const isDisabled = resend.isPending || countdown > 0

  return (
    <button
      onClick={handleResend}
      disabled={isDisabled}
      className={className}
      style={style || styles.button}
      aria-label="重新發送驗證郵件"
      aria-busy={resend.isPending}
      aria-disabled={isDisabled}
    >
      {resend.isPending && <span style={styles.spinner} aria-hidden="true"></span>}
      {countdown > 0 ? `重新發送 (${countdown}秒)` : '重新發送驗證郵件'}
    </button>
  )
}

const styles: Record<string, React.CSSProperties> = {
  button: {
    marginTop: '24px',
    padding: '8px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
}
